import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-0470e161"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "booking-widget" }
const _hoisted_2 = { class: "booking-widget__calendar" }
const _hoisted_3 = {
  key: 0,
  class: "booking-widget__calendar__loader"
}
const _hoisted_4 = { class: "booking-widget__booking" }

import { onMounted, PropType, ref, watch } from 'vue'
import {
  useEventsSlotsFetchMutation,
  useFetchDatesAvailability,
  useServicesFetchMutation,
  useServicesSlotsFetchMutation,
  useTicketsSlotsFetchMutation,
} from '@/queries/public/bookingWidget'
import { useRoute } from 'vue-router'
import WidgetCalendar from '@/components/booking-widget/WidgetCalendar.vue'
import { useI18n } from 'vue-i18n'
import { ServiceType } from '@/enums/services'
import BookingComponent from '@/components/booking-widget/BookingComponent.vue'
import { GroupedOption, StringDate } from '@/models/shared.model'
import {
  CompanyHousingDateAvailability,
  MinimumTimeslot,
} from '@/models/public/service.model'
import { format, formatISO } from 'date-fns'
import { PublicService } from '@/models/public/bookingWidget.model'
import _ from 'lodash'
import startOfDay from 'date-fns/startOfDay'
import endOfDay from 'date-fns/endOfDay'
import {
  getMonthDaysForDate,
  getWeekdaysForDate,
} from '@/utils/overview-calendar'
import isAfter from 'date-fns/isAfter'
import { CalendarType } from '@/models/google.model'
import {
  filterOutSlots,
  findAllOutOfBoundsSelectedSlots,
  findAllSlotsBetweenSlotAndMinDuration,
  findAllSlotsBetweenSlotAndSelectedSlots,
  mapEventSlots,
  mapTicketSlots,
} from '@/utils/booking-widget'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { Order } from '@/models/public/order.model'
import {
  ITicketItemDateAvailability,
  ITicketItemDateAvailabilityBody,
  ITicketItemDatePeriodAvailabilityBody,
} from '@/models/service/serviceTicket.model'
import { formatServerDate } from '@/utils/formatters'
import { useFetchTicketItemDateAvailabilityListMutation } from '@/queries/service/serviceTicket'
import { useFetchCompanyColorPreferencesWithToken } from '@/queries/settings.queries'
import { COLORS } from '@/models/settings.model'


const __default__ = {
  name: 'BookingWidgetView',
}

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  service: {
    type: Object as PropType<PublicService | null>,
    default: null,
  },
},
  setup(__props) {

const props = __props

_useCssVars(_ctx => ({
  "5d68285a": (companyColorPreferences.value?.primary_dark),
  "7e8e5e4a": (companyColorPreferences.value?.primary),
  "501118b2": (companyColorPreferences.value?.primary_light),
  "9a4a3be6": (companyColorPreferences.value?.secondary),
  "6f89f330": (companyColorPreferences.value?.secondary_dark),
  "dfa48036": (companyColorPreferences.value?.primary_light_2)
}))



const i18n = useI18n()
const route = useRoute()

const selectedService = ref<PublicService | null>(null)
const groupedOptions = ref<GroupedOption[]>([])
const selectedStartDate = ref<Date | null>(null)
const selectedEndDate = ref<Date | null>(null)
const selectedAdultCount = ref<number>(1)
const selectedKidCount = ref<number>(0)
const slots = ref<MinimumTimeslot[]>([])
const selectedSlots = ref<MinimumTimeslot[]>([])
const housingAvailabilities = ref<CompanyHousingDateAvailability[]>([])
const ticketAvailabilities = ref<ITicketItemDateAvailability[]>([])
const days = ref(getWeekdaysForDate(new Date()))
const selectedCalendar = ref<CalendarType | null>(null)
const order = ref<Order | null>(null)
const companyToken = ref<string | null>(null)
const companyColorPreferences = ref()

const { mutateAsync: mutateGetServices } = useServicesFetchMutation()
const { mutateAsync: mutateGetSlots } = useServicesSlotsFetchMutation()
const { mutateAsync: mutateGetEventSlots } = useEventsSlotsFetchMutation()
const { mutateAsync: mutateGetTicketSlots } = useTicketsSlotsFetchMutation()
const {
  mutateAsync: mutateGetHousingAvailability,
  isLoading: housingAvailabilitiesLoading,
} = useFetchDatesAvailability()
const { mutateAsync: mutateGetTicketAvailabilities } =
  useFetchTicketItemDateAvailabilityListMutation()
const { mutateAsync: mutateGetCompanyColorPreferences } =
  useFetchCompanyColorPreferencesWithToken(companyToken)

onMounted(async () => {
  companyToken.value = route.query.token as string
  let colors
  if (companyToken.value) {
    const services = await mutateGetServices(companyToken.value as string)
    colors = await mutateGetCompanyColorPreferences()
    groupedOptions.value = groupOptions(services)
  }
  companyColorPreferences.value = colors || { ...COLORS }
})

watch(
  () => props.service,
  () => {
    groupedOptions.value = groupOptions([props.service])
    if (props.service) {
      selectedService.value = props.service
    }
  },
)

watch(
  () => selectedStartDate.value,
  async (newStartDate, prevStartDate) => {
    // For housing, we recalculate days only when no previous start date was specified (initial set)
    if (
      selectedService.value?.type == ServiceType.HOUSING &&
      prevStartDate &&
      newStartDate
    ) {
      return
    }
    handleDaysChange()
  },
)

watch(
  () => selectedService.value,
  () => {
    handleCalendarChange()
    handleDaysChange()
    selectedSlots.value = []
    if (selectedService.value) {
      selectedAdultCount.value = selectedService.value.min_group_size
    }
    if (
      selectedService.value?.type === ServiceType.TICKET &&
      !selectedStartDate.value
    ) {
      selectedStartDate.value = new Date()
    }
  },
)

// HOUSING and EVENT and TICKET
watch(
  () => [
    selectedService.value,
    days.value,
    // When availability logic takes into account bookings already added to cart, then it will make sense to refetch based on order
    // order.value,
  ],
  (newValues, prevValues) => {
    // For some reason getting false positive for changes
    if (
      _.isEqual(newValues[0], prevValues[0]) &&
      _.isEqual(newValues[1], prevValues[1])
    ) {
      return
    }
    if (selectedService.value?.type === ServiceType.HOUSING) {
      fetchHousingAvailability()
    } else if (selectedService.value?.type === ServiceType.EVENT) {
      fetchEvenetSlots()
    } else if (selectedService.value?.type === ServiceType.TICKET) {
      fetchTicketAvailabilities()
      fetchTicketSlots()
      selectedSlots.value = []
      selectedStartDate.value = days.value[0]
    }
  },
)

// EXPERIENCE
watch(
  () => [
    selectedService.value,
    days.value,
    selectedAdultCount.value,
    // When availability logic takes into account bookings already added to cart, then it will make sense to refetch based on order
    // order.value,
  ],
  async (newValues, prevValues) => {
    // For some reason getting false positive for changes
    if (
      _.isEqual(newValues[0], prevValues[0]) &&
      _.isEqual(newValues[1], prevValues[1]) &&
      _.isEqual(newValues[2], prevValues[2])
    ) {
      return
    }
    if (selectedService.value?.type === ServiceType.EXPERIENCE) {
      fetchExperienceSlots()
    }
  },
)

const fetchHousingAvailability = async () => {
  if (days.value.length && selectedService.value) {
    housingAvailabilities.value = await mutateGetHousingAvailability({
      company: selectedService.value.company,
      start: format(days.value[0], 'yyyy-MM-dd'),
      end: format(days.value[days.value.length - 1], 'yyyy-MM-dd'),
      services: [selectedService.value.hash_id],
      group_service: undefined,
    })
  }
}

async function fetchTicketAvailabilities() {
  if (!selectedService.value) {
    ticketAvailabilities.value = []
    return
  }
  try {
    const body:
      | ITicketItemDateAvailabilityBody
      | ITicketItemDatePeriodAvailabilityBody = {
      date: formatServerDate(days.value[0]) as StringDate,
    }
    ticketAvailabilities.value = await mutateGetTicketAvailabilities({
      hashId: selectedService.value.hash_id,
      body,
    })
  } catch (e) {
    console.error(e)
  }
}

const fetchExperienceSlots = async () => {
  if (!selectedService.value) return

  slots.value = await mutateGetSlots({
    serviceId: selectedService.value.hash_id,
    body: {
      start: formatISO(startOfDay(days.value[0]), {
        representation: 'date',
      }),
      end: formatISO(endOfDay(days.value[days.value.length - 1]), {
        representation: 'date',
      }),
      group_size: selectedAdultCount.value,
    },
  })
}

const fetchEvenetSlots = async () => {
  if (!selectedService.value) return

  const eventSlots = await mutateGetEventSlots({
    serviceId: selectedService.value.hash_id,
    body: {
      start: formatISO(startOfDay(days.value[0]), {
        representation: 'date',
      }),
      end: formatISO(endOfDay(days.value[days.value.length - 1]), {
        representation: 'date',
      }),
    },
  })
  slots.value = mapEventSlots(eventSlots)
}

const fetchTicketSlots = async () => {
  if (!selectedService.value) return

  const ticketSlots = await mutateGetTicketSlots({
    serviceId: selectedService.value.hash_id,
    body: {
      start: formatISO(startOfDay(days.value[0]), {
        representation: 'date',
      }),
      end: formatISO(endOfDay(days.value[days.value.length - 1]), {
        representation: 'date',
      }),
    },
  })
  slots.value = mapTicketSlots(ticketSlots)
}

const groupOptions = (services: PublicService[]) => {
  const grouped: { [key: string]: GroupedOption } = {}

  services.forEach((service) => {
    const translatedType = (() => {
      switch (service.type) {
        case ServiceType.EVENT:
          return i18n.t('serviceType.eventLabel')
        case ServiceType.EXPERIENCE:
          return i18n.t('serviceType.experienceLabel')
        case ServiceType.HOUSING:
          return i18n.t('serviceType.housingLabel')
        case ServiceType.TICKET:
          return i18n.t('serviceType.ticketLabel')
        default:
          return null // Default case to handle unexpected values
      }
    })()

    // Initialize the group if it doesn't exist
    if (translatedType) {
      if (!grouped[translatedType]) {
        grouped[translatedType] = {
          key: translatedType,
          items: [],
        }
      }
      // Add the service name to the corresponding category group
      grouped[translatedType].items.push(service)
    }
  })

  // Convert the grouped object into an array
  return Object.values(grouped)
}

const handleCalendarChange = () => {
  switch (selectedService.value?.type) {
    case ServiceType.HOUSING:
      selectedCalendar.value = CalendarType.MONTH
      break
    case ServiceType.EXPERIENCE:
    case ServiceType.EVENT:
      selectedCalendar.value = CalendarType.WEEK
      break
    case ServiceType.TICKET:
      selectedCalendar.value = CalendarType.DAY
      break
    default:
      selectedCalendar.value = null
  }
}

const handleDaysChange = () => {
  const date = selectedStartDate.value || new Date()
  switch (selectedService.value?.type) {
    case ServiceType.HOUSING:
      days.value = getMonthDaysForDate(date)
      break
    case ServiceType.EXPERIENCE:
    case ServiceType.EVENT:
      days.value = getWeekdaysForDate(date)
      break
    case ServiceType.TICKET:
      days.value = [startOfDay(date)]
      break
    default:
  }
}

const handleSlotClick = (slot: MinimumTimeslot) => {
  let newSlots: MinimumTimeslot[] = []
  switch (selectedService.value?.type) {
    case ServiceType.EVENT:
      newSlots = getNewEventSlots(slot)
      break
    case ServiceType.EXPERIENCE:
      newSlots = getNewExperienceSlots(slot)
      break
    case ServiceType.TICKET:
      newSlots = getNewTicketSlots(slot.id as number)
      break
  }
  handleSelectNewSlots(newSlots, slot)
}

const handleSelectNewSlots = (
  newSlots: MinimumTimeslot[],
  selectedSlot?: MinimumTimeslot,
) => {
  selectedSlots.value = newSlots
  if (
    selectedStartDate.value &&
    newSlots.length === 0 &&
    selectedService.value?.type !== ServiceType.TICKET
  ) {
    // If no selected slots, clear selected date, except for tickets
    selectedStartDate.value = null
  } else if (selectedSlot) {
    // If no selected date, assign it from slot
    selectedStartDate.value = new Date(selectedSlot.start)
  }
}

const handleAddSelectedDate = (day: Date) => {
  const currentStartDate = selectedStartDate.value
  const currentEndDate = selectedEndDate.value

  if ((currentStartDate && currentEndDate) || !currentStartDate) {
    selectedStartDate.value = day
    selectedEndDate.value = null
  } else {
    if (isAfter(day, currentStartDate)) {
      selectedStartDate.value = currentStartDate
      selectedEndDate.value = day
    } else {
      selectedStartDate.value = day
      selectedEndDate.value = currentStartDate
    }
  }
}

const handleTicketItemSelect = (
  slotId: number | undefined,
  shouldUnselectAlreadySelected: boolean,
) => {
  let newSlots: MinimumTimeslot[] = []
  if (slotId) {
    newSlots = getNewTicketSlots(slotId, shouldUnselectAlreadySelected)
  }
  handleSelectNewSlots(newSlots)
}

const getNewExperienceSlots = (slot: MinimumTimeslot) => {
  const clickedSelectedSlot = selectedSlots.value.some((s) =>
    _.isEqual(slot, s),
  )
  if (clickedSelectedSlot) {
    // Slot already selected and will be removed
    const betweenSlots = findAllOutOfBoundsSelectedSlots(
      slot,
      selectedSlots.value,
    )
    return filterOutSlots(selectedSlots.value, [...betweenSlots, slot])
  }
  // If is initial selection
  if (selectedSlots.value.length === 0) {
    return [slot, ...findAllSlotsBetweenSlotAndMinDuration(slot, slots.value)]
  }
  return [
    ...selectedSlots.value,
    ...findAllSlotsBetweenSlotAndSelectedSlots(
      slot,
      selectedSlots.value,
      slots.value,
    ),
    slot,
  ]
}

const getNewEventSlots = (slot: MinimumTimeslot) => {
  return slots.value.filter((s) => _.isEqual(slot, s))
}

const getNewTicketSlots = (
  slotId: number,
  shouldUnselectAlreadySelected = true,
): MinimumTimeslot[] => {
  const clickedSelectedSlot = selectedSlots.value.some((s) => s.id === slotId)
  if (clickedSelectedSlot && shouldUnselectAlreadySelected) {
    return selectedSlots.value.filter((s) => s.id !== slotId)
  }
  return [...selectedSlots.value, ...slots.value.filter((s) => s.id === slotId)]
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (
          selectedService.value?.type === _unref(ServiceType).HOUSING &&
          _unref(housingAvailabilitiesLoading)
        )
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(LoadingSpinner)
          ]))
        : (_openBlock(), _createBlock(WidgetCalendar, {
            key: 1,
            "selected-start-date": selectedStartDate.value,
            "onUpdate:selected-start-date": _cache[0] || (_cache[0] = ($event: any) => ((selectedStartDate).value = $event)),
            "selected-end-date": selectedEndDate.value,
            "onUpdate:selected-end-date": _cache[1] || (_cache[1] = ($event: any) => ((selectedEndDate).value = $event)),
            service: selectedService.value,
            "selected-slots": selectedSlots.value,
            slots: slots.value,
            days: days.value,
            "selected-calendar": selectedCalendar.value,
            "housing-availabilities": housingAvailabilities.value,
            "ticket-availabilities": ticketAvailabilities.value,
            onOnSlotClick: handleSlotClick,
            onOnDaysChange: _cache[2] || (_cache[2] = (newDays) => (days.value = newDays)),
            onOnAddSelectedDate: handleAddSelectedDate
          }, null, 8, ["selected-start-date", "selected-end-date", "service", "selected-slots", "slots", "days", "selected-calendar", "housing-availabilities", "ticket-availabilities"]))
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(BookingComponent, {
        "selected-start-date": selectedStartDate.value,
        "onUpdate:selected-start-date": _cache[3] || (_cache[3] = ($event: any) => ((selectedStartDate).value = $event)),
        "selected-end-date": selectedEndDate.value,
        "onUpdate:selected-end-date": _cache[4] || (_cache[4] = ($event: any) => ((selectedEndDate).value = $event)),
        "selected-service": selectedService.value,
        "onUpdate:selected-service": _cache[5] || (_cache[5] = ($event: any) => ((selectedService).value = $event)),
        "selected-adult-count": selectedAdultCount.value,
        "onUpdate:selected-adult-count": _cache[6] || (_cache[6] = ($event: any) => ((selectedAdultCount).value = $event)),
        "selected-kid-count": selectedKidCount.value,
        "onUpdate:selected-kid-count": _cache[7] || (_cache[7] = ($event: any) => ((selectedKidCount).value = $event)),
        order: order.value,
        "onUpdate:order": _cache[8] || (_cache[8] = ($event: any) => ((order).value = $event)),
        "grouped-options": groupedOptions.value,
        "selected-slots": selectedSlots.value,
        "housing-availabilities": housingAvailabilities.value,
        "ticket-availabilities": ticketAvailabilities.value,
        onOnTimeslotSelect: handleTicketItemSelect
      }, null, 8, ["selected-start-date", "selected-end-date", "selected-service", "selected-adult-count", "selected-kid-count", "order", "grouped-options", "selected-slots", "housing-availabilities", "ticket-availabilities"])
    ])
  ]))
}
}

})